import type { WorkflowDefinition } from "@package-api/workflow-definition.types";
import { validateWorkflowId } from "@orchestration/definitions/validation/workflow-id-validation";
import type { WorkflowerGlobal } from "./registry.types";

const registryGlobal = globalThis as WorkflowerGlobal;
const registry = (registryGlobal.__supieriorWorkflowerRegistry ??= {
  workflows: [],
  workflowById: new Map<string, WorkflowDefinition>(),
  listeners: new Set<(workflow: WorkflowDefinition) => void>(),
});
registry.listeners ??= new Set<(workflow: WorkflowDefinition) => void>();

export function addWorkflowToGlobalRegistry(workflow: WorkflowDefinition): void {
  validateWorkflowId(workflow.id);

  const existingWorkflow = registry.workflowById.get(workflow.id);
  if (existingWorkflow !== undefined) {
    if (existingWorkflow === workflow || workflowDefinitionsEqual(existingWorkflow, workflow))
      return;

    throw new Error(`Workflow id already registered with a different definition: ${workflow.id}`);
  }

  registry.workflows.push(workflow);
  registry.workflowById.set(workflow.id, workflow);
  for (const listener of registry.listeners) listener(workflow);
}

export function listWorkflows(): WorkflowDefinition[] {
  return [...registry.workflows];
}

export function findWorkflow(workflowId: string): WorkflowDefinition | undefined {
  return registry.workflowById.get(workflowId);
}

export function onWorkflowRegistered(listener: (workflow: WorkflowDefinition) => void): () => void {
  registry.listeners.add(listener);
  return () => registry.listeners.delete(listener);
}

function workflowDefinitionsEqual(left: WorkflowDefinition, right: WorkflowDefinition): boolean {
  return deepEqual(left, right);
}

function deepEqual(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (typeof left !== typeof right) return false;
  if (left === null || right === null) return false;
  if (typeof left !== "object") return false;

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    return left.every((value, index) => deepEqual(value, right[index]));
  }

  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const leftKeys = Object.keys(leftRecord).sort();
  const rightKeys = Object.keys(rightRecord).sort();
  if (leftKeys.length !== rightKeys.length) return false;

  return leftKeys.every(
    (key, index) => key === rightKeys[index] && deepEqual(leftRecord[key], rightRecord[key]),
  );
}
